import { RewardImage } from './RewardImage';
import { getRewardImageCandidates, getRewardName } from '../lib/rewardImage';

/**
 * The gift-card plate — one voucher-shaped visual shared by the catalogue grid,
 * the product page and the cart, so a card looks like the same object wherever
 * the customer meets it.
 *
 * Two things the provider does not give us, and which are therefore invented
 * here rather than faked from data:
 *
 *   the tint  — Razorpay ships no brand colour. A flat white plate made 276
 *               cards look identical, so the background is a soft two-stop
 *               gradient derived from the brand name. Deterministic, so a card
 *               keeps its colour across pages and reloads.
 *   the strip — "GIFT CARD" along the bottom is what makes a bare logo read as
 *               a voucher instead of a failed image load. Half the catalogue has
 *               no usable artwork at all, so this carries a lot of weight.
 *
 * The tint is deliberately pale: brand logos arrive as dark-on-transparent PNGs
 * and would disappear on the saturated plates the reference screenshots show.
 */

/** Stable 32-bit hash. Only needs to spread names across hues, not be secure. */
function hashName(name) {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (Math.imul(h, 31) + name.charCodeAt(i)) >>> 0;
  return h;
}

export function giftCardTint(name = '') {
  const hue = hashName(name) % 360;
  return `linear-gradient(135deg, hsl(${hue} 52% 96%) 0%, hsl(${(hue + 38) % 360} 48% 91%) 100%)`;
}

/**
 * @param {object} reward     a raw catalogue reward, or anything carrying
 *                            `name` + `display_image_candidates`
 * @param {'sm'|'md'|'lg'} size
 */
export function GiftCardTile({ reward, size = 'md', className = '' }) {
  const name = getRewardName(reward) || reward?.name || '';
  const candidates = getRewardImageCandidates(reward);

  const pad = size === 'sm' ? 'p-3 pb-6' : size === 'lg' ? 'p-8 pb-12' : 'p-6 pb-9';
  const strip = size === 'sm' ? 'text-[8px] py-0.5' : 'text-[10px] py-1.5';

  return (
    <div
      className={`relative aspect-[16/10] w-full overflow-hidden rounded-2xl ring-1 ring-slate-900/[0.06] ${className}`}
      style={{ background: giftCardTint(name) }}
    >
      {/*
        `h-full w-full object-contain`, not `max-h-full max-w-full`. A flex item
        defaults to `min-width: auto`, which outranks a max-width and lets a wide
        logo — the boAt wordmark being the worst — refuse to shrink and spill
        clean out of the card. Sizing the box and letting object-fit letterbox
        inside it cannot overflow.
      */}
      <div className={`absolute inset-0 flex items-center justify-center ${pad}`}>
        <RewardImage
          candidates={candidates}
          name={name}
          size={size === 'sm' ? 'plateSm' : 'plate'}
          className="h-full w-full object-contain"
        />
      </div>

      <span
        className={`absolute inset-x-0 bottom-0 bg-white/65 text-center font-body font-bold uppercase tracking-[0.2em] text-slate-500 backdrop-blur-[2px] ${strip}`}
      >
        Gift Card
      </span>
    </div>
  );
}

export default GiftCardTile;
